import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../lib/use-api';
import { formatDateTime } from '../lib/format';
import { EmptyNotice, ErrorNotice, Loading } from './Feedback';

/** Khớp `InteractionRow` bên delivery-api — trả nguyên snake_case từ DB. */
interface InteractionRow {
  sub_content_id: string;
  question: string | null;
  response: string | null;
  correct: number | null;
  score_raw: string | null;
  score_max: string | null;
  answered_at: string;
}

/**
 * Chi tiết từng câu của một lượt làm bài.
 *
 * Báo cáo trước đây dừng ở điểm tổng, nên giáo viên thấy "6/10" mà không biết
 * học viên sai câu nào — phần có giá trị nhất của dữ liệu xAPI đã thu thập
 * nhưng không có đường xem.
 */
export function InteractionsDialog({
  attemptId,
  learnerName,
  onClose,
}: {
  attemptId: string;
  learnerName: string;
  onClose: () => void;
}): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const rows = useApi<{ rows: InteractionRow[] }>(
    `/api/reports/attempts/${attemptId}/interactions`,
  );

  // DECIMAL về từ mysql2 là CHUỖI — "10" < "5" khi so theo từ điển. Number()
  // ngay tại biên, đúng cạm bẫy đã ghi trong CLAUDE.md.
  const score = (raw: string | null, max: string | null): string => {
    if (raw === null) return '—';
    return max === null ? String(Number(raw)) : `${Number(raw)}/${Number(max)}`;
  };

  return (
    <dialog ref={dialogRef} className="dialog dialog--wide" onClose={onClose}>
      <h2>{t('report.interactionsHeading')}</h2>
      <p className="muted">{learnerName}</p>

      {rows.loading ? <Loading /> : null}
      {rows.error ? <ErrorNotice error={rows.error} onRetry={rows.reload} /> : null}

      {rows.data && !rows.loading ? (
        rows.data.rows.length === 0 ? (
          <EmptyNotice message={t('report.noInteractions')} />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t('report.column.question')}</th>
                <th scope="col">{t('report.column.response')}</th>
                <th scope="col">{t('report.column.correct')}</th>
                <th scope="col">{t('report.column.score')}</th>
                <th scope="col">{t('report.column.answeredAt')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.data.rows.map((row) => (
                <tr key={row.sub_content_id}>
                  <td>{row.question ?? '—'}</td>
                  <td>{row.response ?? '—'}</td>
                  <td>
                    {row.correct === null
                      ? '—'
                      : Number(row.correct) === 1
                        ? t('report.correctYes')
                        : t('report.correctNo')}
                  </td>
                  <td>{score(row.score_raw, row.score_max)}</td>
                  <td>{formatDateTime(row.answered_at, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}

      <footer className="dialog__footer">
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.close')}
        </button>
      </footer>
    </dialog>
  );
}
